import React from 'react';
import I18n from 'i18n-js';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import gql from 'graphql-tag';
import { ChildProps } from 'react-apollo';
import { Link, WithRouterProps } from 'react-router';
import {
  CoreCreditCardWalletIndex,
} from '../schemas/rql';
import { useCreditCardForm } from './credit_card_wallet_modal';
import { CheckoutCreditCardWalletFragment } from './checkout_credit_card_wallet';
import { Paths } from './shared/url_helpers';

import CreditCardWalletRow, { CreditCardWalletRowFragment } from './credit_card_wallet_row';
import { RCRadioCardGroup } from '@reverbdotcom/commons/src/rc/forms/RCRadioCardGroup';
import SiteModulePlaceholder from '@reverbdotcom/commons/src/components/site_module_placeholder';
import { RCButton } from '@reverbdotcom/commons/src/rc/RCButton';

type Props = ChildProps<WithRouterProps, CoreCreditCardWalletIndex.Query>;

export function CreditCardWalletIndex(props: Props) {
  const form = useCreditCardForm();
  const { query } = props.location;
  const { selectedShippingAddressId } = form;

  const creditCards = props.data?.me?.creditCards || [];
  const initialCreditCardSelection = query.selected_card_id
    ? query.selected_card_id
    : creditCards[0]?.id;

  const [cardId, setCardId] = React.useState(initialCreditCardSelection);

  function onCardChange({ target: { value: creditCardId } }) {
    setCardId(creditCardId);

    const selectedCardPath = Paths.checkoutCreditCardWallet.expand({
      checkoutId: props.params.id,
      selected_card_id: creditCardId,
      selected_shipping_address_id: selectedShippingAddressId,
    });

    props.router.push(selectedCardPath);
  }

  function onSubmit(e) {
    e.preventDefault();
    form.onSelect(cardId);
  }

  if (props.data?.loading) {
    return (
      <>
        <SiteModulePlaceholder />
        <SiteModulePlaceholder />
      </>
    );
  }

  return (
    <form id={form.id} onSubmit={onSubmit}>
      <RCRadioCardGroup
        id="credit-card-wallet"
        value={cardId}
        onChange={onCardChange}
        name="credit-card-wallet"
        label=""
      >
        {creditCards.map((creditCard) => (
          <RCRadioCardGroup.Option
            key={creditCard.id}
            value={creditCard.id}
            label={
              <CreditCardWalletRow
                creditCard={creditCard}
              />
            }
          />
        ))}
      </RCRadioCardGroup>
      <Link to={Paths.checkoutCreateCreditCard.expand({
        checkoutId: props.params.id,
        selected_card_id: cardId,
        selected_shipping_address_id: selectedShippingAddressId,
      })}>
        <RCButton>
          {I18n.t('discovery.creditCardWallet.modal.addNew.title')}
        </RCButton>
      </Link>
    </form>
  );
}

export const CreditCardWalletLayoutFragment = gql`
  fragment CreditCardWalletLayoutFields on rql_Me {
    ...CreditCardWalletRowFields
  }
  ${CreditCardWalletRowFragment}
`;

const withQuery = withGraphql<Props, CoreCreditCardWalletIndex.Query>(
  gql`
    query Core_CreditCardWallet_Index($id: String) {
      countries {
        countries {
          countryCode
          name
          subregionRequired
          subregions {
            code
            name
          }
        }
      }
      me {
        uuid
        ...CreditCardWalletLayoutFields
      }
      checkout(input: {id: $id}) {
        id
        _id
        ...CheckoutCreditCardWalletFields
      }
    }
    ${CreditCardWalletLayoutFragment}
    ${CheckoutCreditCardWalletFragment}
  `,
  {
    options: (ownProps) => {
      return {
        variables: {
          id: ownProps.params.id,
        },
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      };
    },
  },
);

export default withQuery(CreditCardWalletIndex);
